/**
 * Copyright Blue Billywig 2018+
 */

import { Component, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { LocalStorageService } from 'angular-2-local-storage';

import { ReduxService } from '../../../services/redux.service';

import { InteractivityEditorService } from './services/interactivity-editor.service';
import { InteractivityWidgetSelectionService } from './services/interactivity-widget-selection.service';
import { Polyfill } from '../../../shared/utils/polyfill.util';
import { HotkeysService, Hotkey } from 'angular2-hotkeys';
import { BbDragEvent } from '../../../directives/bb-drag.directive';
import { KeyframeSelection, InteractivityWidgetAnimationService } from './services/interactivity-widget-animation.service';
import { InteractivityMediaService } from './services/interactivity-media.service';
import { InteractivityWidgetService } from './services/interactivity-widget.service';
import { DesignDimensions, InteractivityDesignDimensionsService } from './services/interactivity-design-dimensions.service';
import { InteractivityStageService } from './services/interactivity-stage.service';
import { IWidgetSelection } from './interfaces/IWidgetSelection';
import { Subscription } from 'rxjs';

@Component({
	selector: 'interactivity-editor',
	templateUrl: './interactivity-editor.component.html',
	styleUrls: ['./interactivity-editor.component.scss', './themes/material.theme.scss']
})

// this component's state is managed by redux in the interactivity-editor.service.ts
export class InteractivityEditorComponent implements OnInit, OnDestroy {
	@Output() gridResizeStarted: EventEmitter<null> = new EventEmitter();
	@Output() gridResizeEnded: EventEmitter<null> = new EventEmitter();

	trackAreaResizerTop = -1;
	keyframeSelection: KeyframeSelection;
	gridTemplateRowsStyle: string;

	private _designWidth = 768;
	private _designHeight = 432;
	private _activeArea: string;

	private subscriptions = new Subscription();

	constructor(private reduxService: ReduxService,
		private localStorageService: LocalStorageService,
		public interactivityEditorService: InteractivityEditorService,
		private interactivityMediaService: InteractivityMediaService,
		private interactivityWidgetService: InteractivityWidgetService,
		private interactivityDesignDimensionsService: InteractivityDesignDimensionsService,
		private interactivityStageService: InteractivityStageService,
		private hotkeysService: HotkeysService,
		private interactivityWidgetAnimationService: InteractivityWidgetAnimationService,
		private widgetSelectionService: InteractivityWidgetSelectionService) {

		Polyfill.getRequestAnimationFrame();
	}

	set activeArea(area) {
		if (this._activeArea !== area) {
			this._activeArea = area;
			this.interactivityEditorService.setActiveArea(area);
		}
	}

	get activeArea() {
		return this._activeArea;
	}

	ngOnInit() {
		this.bindHotKeys();
		this.gridTemplateRowsStyle = '640 1fr'; // 'calc(100% - ' + this.tracksRowHeight + 'px) ' + this.tracksRowHeight + 'px';
		this.interactivityEditorService.activateReduxSubscription(); // (re)init service
		this.subscriptions.add(this.widgetSelectionService.widgetSelectionSubject.subscribe((selection: IWidgetSelection) => {
			switch (selection && selection.widgets && selection.widgets.length && selection.origin && selection.origin.area) {
				case 'tracks':
					this.activeArea = 'bottom-area';
					break;
				case 'stage':
					this.activeArea = 'central-area';
					break;
			}
		}));

		this.subscriptions.add(this.interactivityDesignDimensionsService.designDimensionsSubject.subscribe((designDimensions: DesignDimensions) => {
			this.designDimensionsChanged(designDimensions);
		}));
	}

	ngOnDestroy() {
		// remove local storage items used in interactivity editor to prevent local storage from flooding
		const lcKeys: string[] = this.localStorageService.keys();
		lcKeys.forEach(key => {
			if (key.substring(0, 6) === 'ovp-ie') {
				this.localStorageService.remove(key);
			}
		});
		this.interactivityEditorService.cleanup();
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	bindHotKeys() {
		// TODO cleanup hotkey bindings?
		this.hotkeysService.add(new Hotkey('escape', (event: KeyboardEvent): boolean => {
			this.widgetSelectionService.clearSelectedWidgets();
			return false;
		}));

		this.hotkeysService.add(new Hotkey(['del', 'backspace'], (event: KeyboardEvent): boolean => {
			if (this.interactivityWidgetAnimationService.keyframeSelection.selected.length) {
				this.interactivityWidgetAnimationService.removeSelectedKeyframe();
			} else {
				this.widgetSelectionService.deleteSelectedWidgets();
			}
			return false;
		}));

		this.hotkeysService.add(new Hotkey('mod+a', (event: KeyboardEvent): boolean => {
			this.widgetSelectionService.selectAllWidgets();
			return false;
		}));

		this.hotkeysService.add(new Hotkey('mod+z', (event: KeyboardEvent): boolean => {
			this.reduxService.dispatch({ type: 'UNDO' });
			return false;
		}));

		this.hotkeysService.add(new Hotkey('mod+shift+z', (event: KeyboardEvent): boolean => {
			this.reduxService.dispatch({ type: 'REDO' });
			return false;
		}));

		// media controls

		this.hotkeysService.add(new Hotkey('space', (event: KeyboardEvent): boolean => {
			this.interactivityMediaService.togglePlayPause();
			return false;
		}));

		this.hotkeysService.add(new Hotkey('left', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.arrow('left');
			} else {
				this.interactivityMediaService.previousFrame();
			}
			return false;
		}));

		const relativeTime = 0.5;

		this.hotkeysService.add(new Hotkey('shift+left', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.arrow('left', true);
			} else {
				this.interactivityMediaService.seekToRelativeTime(-relativeTime);
			}
			return false;
		}));

		this.hotkeysService.add(new Hotkey('right', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.arrow('right');
			} else {
				this.interactivityMediaService.nextFrame();
			}
			return false;
		}));

		this.hotkeysService.add(new Hotkey('shift+right', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.arrow('right', true);
			} else {
				this.interactivityMediaService.seekToRelativeTime(relativeTime);
			}
			return false;
		}));

		this.hotkeysService.add(new Hotkey('up', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.arrow('up');
			}
			return false;
		}));

		this.hotkeysService.add(new Hotkey('shift+up', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.arrow('up', true);
			}
			return false;
		}));

		this.hotkeysService.add(new Hotkey('down', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.arrow('down');
			}
			return false;
		}));

		this.hotkeysService.add(new Hotkey('shift+down', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.arrow('down', true);
			}
			return false;
		}));

		this.hotkeysService.add(new Hotkey('mod+c', (event: KeyboardEvent): boolean => {
			this.interactivityWidgetService.copy(this.widgetSelectionService.widgetSelection.widgets);
			return false;
		}));

		// this.hotkeysService.add(new Hotkey('mod+v', (event: KeyboardEvent): boolean => {
		// moved this to the paste event to check that data first
		// }));

		this.hotkeysService.add(new Hotkey('mod+j', (event: KeyboardEvent): boolean => {
			if (
				this.widgetSelectionService.widgetSelection && this.widgetSelectionService.widgetSelection.widgets
				&& this.widgetSelectionService.widgetSelection.widgets.length
			) {
				const deltas = {
					xpos: ((20 / this._designWidth) * 100) + 'px',
					ypos: ((20 / this._designHeight) * 100) + 'px'
				};
				const newIds = this.interactivityWidgetService.duplicateWidgets(this.widgetSelectionService.widgetSelection.widgets, deltas, true);
				setTimeout(() => {
					this.widgetSelectionService.clearSelectedWidgets();
					this.widgetSelectionService.addSelectedWidgetByIdMulti(newIds);
				}, 50);
			}
			return false;
		}));

		// mod+shift+0 doesn't seem to work on windows (some windows native binding)
		this.hotkeysService.add(new Hotkey('mod+0', (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.zoom('toFit');
				return false;
			} else {
				return true;
			}
		}));

		this.hotkeysService.add(new Hotkey(['mod+plus', 'mod+=', 'mod+shift+plus', 'mod+shift+='], (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.zoom('in');
				return false;
			}
			return true;
		}));

		this.hotkeysService.add(new Hotkey(['mod+-', 'mod+shift+-'], (event: KeyboardEvent): boolean => {
			if (this.activeArea === 'central-area') {
				this.interactivityStageService.zoom('out');
				return false;
			}
			return true;
		}));
	}

	trackAreaResizerDragging(ev: BbDragEvent) {
		this.trackAreaResizerTop = ev.top;
	}

	private designDimensionsChanged(designDimensions: any) {
		this._designWidth = designDimensions.width;
		this._designHeight = designDimensions.height;
	}
}
